<script lang="ts">
    import { Button } from "flowbite-svelte";
    import { twMerge } from "tailwind-merge";
    import type {HTMLAnchorAttributes, HTMLAttributes, HTMLButtonAttributes} from "svelte/elements";
    import type { Color } from "../types";
    import { State } from "../state.svelte";

    type FlowbiteButtonDef = {
        props: ({
            pill?: boolean;
            outline?: boolean;
            size?: "xs" | "sm" | "md" | "lg" | "xl";
            shadow?: boolean;
            tag?: "a" | "button" | "label";
            checked?: boolean;
        } & Omit<HTMLAnchorAttributes, "type"> & {
            href?: string | undefined;
        }) | ({
            pill?: boolean;
            outline?: boolean;
            size?: "xs" | "sm" | "md" | "lg" | "xl";
            shadow?: boolean;
            tag?: "a" | "button" | "label";
            checked?: boolean;
        } & HTMLButtonAttributes & {
            disabled?: HTMLButtonAttributes["disabled"];
            type?: HTMLButtonAttributes["type"];
        });
        events: {
            click: MouseEvent;
            change: Event;
            keydown: KeyboardEvent;
            keyup: KeyboardEvent;
            touchstart: TouchEvent;
            touchend: TouchEvent;
            touchcancel: TouchEvent;
            mouseenter: MouseEvent;
            mouseleave: MouseEvent;
        } & {
            [evt: string]: CustomEvent<any>;
        };
        slots: {
            default: {};
        };
    };

    type FlowbiteButtonProps = FlowbiteButtonDef["props"];

    interface Props extends HTMLAttributes<HTMLButtonElement> {
        children: () => any;
        class?: HTMLAttributes<any>["class"];
        defaultColor?: Color;
        color?: Color;
        frappeColor?: Color;
        lightText?: boolean;
        vScale?: boolean;
    }

    let {
        children,
        class: clazz = "",
        lightText = false,
        color = "default",
        vScale = false,
        ...others
    }: Props & FlowbiteButtonProps = $props();

    if (clazz == null) clazz = "";

    const colorClass: Record<Color, string> = {
        red: "frappe:bg-ctp-red light:bg-red-500 black:bg-red-500 light:text-white black:text-white",
        green: "frappe:bg-ctp-green light:bg-green-500 black:bg-green-500 light:text-white",
        yellow: "frappe:bg-ctp-yellow light:bg-yellow-500",
        blue: "frappe:bg-ctp-blue light:bg-blue-500 black:bg-blue-500 light:text-white",
        pink: "frappe:bg-ctp-pink light:bg-pink-500",
        teal: "frappe:bg-ctp-teal",
        rosewater: "frappe:bg-ctp-rosewater",
        crust: "frappe:bg-ctp-crust bg-crust-500",
        lavender: "frappe:bg-ctp-lavender",
        default: "frappe:bg-ctp-pink light:bg-primary-500 black:bg-primary-500 light:text-white black:text-white",
    };

    function getColor() {
        if (colorClass[color] != undefined) {

            let usableColors: Map<Color, string> = $state(new Map());

            Object.keys(colorClass).forEach((colorStr: string) => {
                const color = colorStr as Color;
                if (color != "default" && (colorClass[color]).includes(State.options.theme + ":"))
                    usableColors.set(color, colorClass[color]);
            });

            if (usableColors.get(color))
                return usableColors.get(color);
        }
        return colorClass["default"];
    }

    const textColor = !lightText ? "frappe:text-ctp-base" : "frappe:text-ctp-subtext0";

    const sizingClasses = "px-3 py-2 text-xs v4xs:!px-5 v4xs:!py-2.5 v4xs:text-sm";

    const buttonClass = "focus-within:ring-0 hover:brightness-90 " + (vScale ? "py-1.5 px-3 vxs:py-2.5 vxs:px-5" : "");

</script>

<Button color="none" class={twMerge(sizingClasses, buttonClass, textColor, getColor(), clazz.toString())}
        {...others}>
    {@render children()}
</Button>